import { EmptyReportOverlay } from '@report/components';
import { useSearchExamEventReportItemsQuery } from '@report/graphql';
import { type GridSortModel, NoRowsOverlay } from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';
import { dayjs, toDayRange } from '@shared/utils';
import { useMemo } from 'react';

import { ExamEventOverviewReportDataGrid } from '../../components';
import { EXAM_TYPE_LABEL, REGISTRATION_TYPE_LABEL } from '../../constants';
import { useExamEventOverviewReportSearchParams } from '../../hooks';

export default function ExamEventOverviewReportList() {
  const [
    {
      page,
      pageSize,
      sortBy,
      sortOrder,
      startDate,
      endDate,
      examCenter,
      examType,
      registrationType,
      province,
    },
    { changePageNumber, changePageSize, changeSorting },
  ] = useExamEventOverviewReportSearchParams();

  const isSearchEmpty =
    !startDate &&
    !endDate &&
    !examCenter.length &&
    !examType.length &&
    !registrationType.length &&
    !province.length;

  const { startedAt, endedAt } = toDayRange(startDate, endDate);

  const { data, loading } = useSearchExamEventReportItemsQuery({
    variables: {
      searchExamEventReportItemsInput: {
        filter: {
          examCenterCodes: examCenter.includes('all') ? null : examCenter,
          examTypes: examType,
          registrationTypes: registrationType,
          provinceIds: province.includes('all') ? null : province,
          startedAt: startedAt as Date,
          endedAt: endedAt as Date,
        },
        paging: {
          currentPage: page,
          pageSize,
        },
        order: {
          field: sortBy,
          type: ORDER_TYPE[sortOrder],
        },
      },
    },
    skip: isSearchEmpty,
  });

  const { rows, total } = useMemo(() => {
    const examEventReportItems = [
      ...(data?.reportSearchExamEventReportItems.examEventReportItems ?? []),
    ];

    return {
      rows: examEventReportItems.map((item) => ({
        id: item.id,
        startedAt: dayjs(item.startedAt).format('DD-MM-BBBB'),
        examTime:
          dayjs(item.startedAt).format('HH:mm') + ' - ' + dayjs(item.endedAt).format('HH:mm'),
        examCenterCode: item.examCenterCode,
        regType: REGISTRATION_TYPE_LABEL[item.registrationType],
        province: item.province?.nameTh ?? '',
        location: item.location?.nameTh ?? '',
        examType: EXAM_TYPE_LABEL[item.examType],
        totalSeats: item.seatCount,
        registeredSeats: item.examineeCount,
        attendedSeats: item.attendedExamineeCount,
      })),
      total: data?.reportSearchExamEventReportItems.total ?? 0,
    };
  }, [data?.reportSearchExamEventReportItems]);

  const handleSortModelChange = (sorting: GridSortModel) => {
    const field = sorting[0]?.field ?? sortBy;
    const sort = sorting[0]?.sort ?? sortOrder;
    changeSorting({ sortBy: field, sortOrder: sort });
  };

  return (
    <ExamEventOverviewReportDataGrid
      initialState={{
        sorting: {
          sortModel: [
            {
              field: sortBy,
              sort: sortOrder,
            },
          ],
        },
      }}
      sortingMode='server'
      onSortModelChange={handleSortModelChange}
      loading={loading}
      slotProps={{
        footer: {
          total,
          pagination: {
            count: total,
            page: page,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              const pageSize = parseInt(event.target.value);
              return changePageSize(pageSize);
            },
          },
        },
      }}
      rows={rows}
      slots={{
        noRowsOverlay: isSearchEmpty ? EmptyReportOverlay : NoRowsOverlay,
      }}
    />
  );
}
